import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';

/**
 * A number input that **clears the visible `0`** when the user
 * focuses it, so they can start typing immediately without
 * back­spacing the placeholder zero. On blur, an empty value
 * collapses back to `0` (or `defaultEmpty` when set) so the form
 * always carries a number.
 *
 * Why a wrapper instead of a one-line `onFocus={(e) => e.target.select()}`?
 * `select()` highlights the existing `0` but leaves it visible until
 * the user actually types — for fields where the user just wants a
 * fresh blank tile (cost / price / qty), the lingering zero feels
 * like noise. This component fully blanks the displayed text while
 * focused.
 *
 * Internally:
 *  - `value` (number) is the controlled value from the parent form.
 *  - `text` (string) is the local mirror used while focused; it can
 *    diverge from `value` (e.g. user has typed `1.0` mid-edit).
 *  - On focus: if the incoming value is `0`, we wipe `text` to
 *    `""` AND call `select()` (defensive — covers the case where the
 *    user tabs in without clicking).
 *  - On blur: parse `text`. Empty / NaN → fall back to
 *    `defaultEmpty` (default `0`); otherwise pass the parsed
 *    number to `onValueChange`.
 *
 * Drop-in replacement for `<input type="number">` everywhere in the
 * panel forms.
 */
export interface NumberFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: number | null | undefined;
  onValueChange: (next: number) => void;
  /** Fallback when the user blurs an empty field. Default `0`. */
  defaultEmpty?: number;
}

export function NumberField({
  value,
  onValueChange,
  defaultEmpty = 0,
  onFocus,
  onBlur,
  ...rest
}: NumberFieldProps) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState<string>(() =>
    value == null || Number.isNaN(value) ? '' : String(value),
  );
  const ref = useRef<HTMLInputElement>(null);

  // Keep the displayed text in sync with the parent's value when the
  // field isn't focused (parent reset, edit-mode prefill, etc.).
  useEffect(() => {
    if (!focused) {
      setText(value == null || Number.isNaN(value) ? '' : String(value));
    }
  }, [value, focused]);

  return (
    <input
      ref={ref}
      type="number"
      value={focused ? text : value == null || Number.isNaN(value) ? '' : String(value)}
      onFocus={(e) => {
        setFocused(true);
        // The whole point: a fresh `0` becomes empty so the user
        // doesn't have to back­space before typing. Non-zero values
        // get select-all so a single keystroke replaces them.
        if (Number(value) === 0) {
          setText('');
        } else {
          // Defer select() until after React commits the input value
          // from `value` (the unfocused branch above). Without the
          // microtask the selection sometimes lands on `text` which
          // can lag one render behind.
          requestAnimationFrame(() => e.currentTarget?.select?.());
        }
        onFocus?.(e);
      }}
      onChange={(e) => {
        const raw = e.currentTarget.value;
        setText(raw);
        /* Also push the parsed value to the parent on every keystroke
           (not just on blur). Without this, a user who types "100" and
           immediately clicks Save triggers a blur → state-update →
           click race; React 18's automatic batching lets the click
           handler read the stale closure (`totalAmount: 0`) before the
           blur's setForm commits, so the saved record carries 0. Bug
           ref: AI Agent 2026-05-21 — "purchase order amount not save
           it show only zero". */
        const trimmed = raw.trim();
        if (trimmed === '') return; // leave parent untouched until blur
        const n = Number(trimmed);
        if (Number.isFinite(n)) onValueChange(n);
      }}
      onBlur={(e) => {
        setFocused(false);
        const trimmed = e.currentTarget.value.trim();
        if (trimmed === '') {
          onValueChange(defaultEmpty);
        } else {
          const n = Number(trimmed);
          onValueChange(Number.isFinite(n) ? n : defaultEmpty);
        }
        onBlur?.(e);
      }}
      {...rest}
    />
  );
}

export default NumberField;
